
import { computed, defineComponent, ref, reactive, onMounted, watch } from "vue";
import ApiService from "@/core/services/ApiService";
import { ElMessage, ElMessageBox } from 'element-plus';
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { formatJson } from '@/utils';
import { exportJson2Excel } from '@/utils/excel';
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";

interface IPagination {
  enable: boolean;
  page: number;
  pageCount: number;
  total: number;
  rowsPerPage: number;
}

export default defineComponent({
  name: "datatable",
  props: {
    apiUrl: {type: String, required: true },  //后端请求地址
    tableHeader: { type: Array, required: true },  //表格表头
    defaultSort: {type: String, default: "-id"},  //默认排序
    data: {type: Object, default: () => { return {} }},  //添加修改的提交数据
    rules: {type: Object, default: () => { return {} }},  //提交数据校验
    filters: {type: Object, default: () => { return{search: ''}}},  //表格展示筛选条件，默认{search: ''}
    enableItemsPerPageDropdown: {type: Boolean, default: true},  //是否展示分页
    rowsPerPage: Number,  //分页数
    title: {type: String, default: '设置'},  //提交数据标题
    tableHeaderAction: Function,  //根据数据动态调整表格字段方法
    addAction: Function,  //添加覆盖方法
    editAction: Function,  //修改覆盖方法
    submitAction: Function,  //提交前置方法
    submitAfterAction: Function,  //提交后置方法
    step: {type: Number, default: 1},  //提交当前步骤
    maxStep: {type: Number, default: 1},  //提交最大步骤，默认只有1步
    delete: {type: Boolean, default: true},  //是否真删除，默认真删除数据，为假则更新删除标志位
    actionMenu: {type: Boolean, default: true},  //是否出现每行的Action菜单，默认出现
    preLoad: Function,  //载入前执行的方法
    emptyText: {type: String, default: '没有找到和关键词相关的结果！'},  //没有数据时显示的文字
  },
  components: {},
  setup(props, context) {
    const store = useStore();
    // 表格数据
    const tableData = ref<any>([]);
    // 当前排序
    const currentSort = ref<string>(props.defaultSort);
    // 当前顺序：正序false、倒序true
    const reverse = ref<boolean>(false);
    // 分页
    const pagination = ref<IPagination>({
      enable: true,
      page: 1,
      pageCount: 0,
      total: 0,
      rowsPerPage: 10,
    });
    // 默认排序字段
    const sortingField = ref(props.defaultSort);
    // 页面筛选条件
    const filter = reactive(props.filters);
    // 表单数据，props是单向数据流，不能更改
    const formData = reactive(props.data)
    // 表单初始数据
    const initData = JSON.parse(JSON.stringify(props.data))
    // 表格载入延迟
    const loading = ref<boolean>(false);
    // 表单提交延迟
    const btnLoading = ref<boolean>(false);
    // 添加、修改弹层显隐状态
    const dialogAddEdit = ref<boolean>(false);
    // 表单引用
    const formRef = ref<HTMLElement | null>(null);
    // 无数据时显示文字
    const eText = ref<String>(props.emptyText.replace('{search}', filter.search));

    onMounted(() => {
      pagination.value.rowsPerPage = props.rowsPerPage ? props.rowsPerPage : 10;
      for (let t of props.tableHeader) {
        if((t as any).sortingField) {
          sortingField.value = (t as any).sortingField
          break;
        }
      }
      if (props.preLoad != undefined) {
        props.preLoad(loadData);
      } else {
        loadData();
      }
    });

    // 数据载入
    const loadData = () => {
      let p = Object.assign(JSON.parse(JSON.stringify(filter)), {
        ordering: currentSort.value ? currentSort.value : sortingField.value,
        offset: pagination.value.rowsPerPage * (pagination.value.page -1),
        limit: pagination.value.rowsPerPage,
        page: pagination.value.page
      })
      loading.value = true;
      ApiService.get(props.apiUrl, p).then(({data}) => {
        let results;
        if (data.results) {
          results = data.results;
          pagination.value.total = data.count;
          pagination.value.pageCount = Math.ceil(pagination.value.total / pagination.value.rowsPerPage);
        } else {
          results = data;
          pagination.value.enable = false;
        }
        tableData.value = [...results];
        if (results.length > 0 && props.tableHeaderAction != undefined) {
          let fields: Array<string> = []
          for (let key in results[0]) {
            fields.push(key)
          }
          props.tableHeaderAction(fields);
        }
        for (let t of results) {
          for (let k in t) {
            if (/^[-0-9]+\.+[0-9]*$/.test(t[k]) && (k.substr(-5) == '_rate')) {
              t[k] = (parseFloat(t[k]) * 100).toFixed(2)  + '%'
            } else if (/^[-0-9]+\.+[0-9]*$/.test(t[k]) && (k.substr(-7) == '_margin' || k == 'discount')) {
              t[k] = (parseFloat(t[k]) * 100).toFixed(0)  + '%'
            } else if (/^[-0-9]+\.+[0-9]*$/.test(t[k]) && parseFloat(t[k]) + "" == t[k]) {
              t[k] = parseFloat(t[k]).toFixed(2)
            }
          }
        }
        loading.value = false;
      })
    }
    // 分页组件的设置第几页
    const setCurrent = (val) => {
      pagination.value.page = val;
      loadData()
    }
    return {
      tableData,
      loading,
      loadData,
      filter,
      pagination,
      currentSort,
      formData,
      dialogAddEdit,
      formRef,
      btnLoading,
      eText,
      // 分页组件的设置第几页
      setCurrent,
      // 设置分页数
      sizeChange (val) {
        pagination.value.rowsPerPage = parseInt(val.target.value);
        loadData()
      },
      // 表头排序动作
      sort (columnName, sortable) {
        if (!sortable) return;
        currentSort.value = (reverse.value ? "-" : "") + columnName;
        loadData()
        reverse.value = !reverse.value
      },
      // 搜索按钮
      searchItems (event) {
        if (event.target && event.target.tagName == 'BUTTON' || event.target.tagName == "I") {
          // pass
        } else if (event.target && event.target.tagName == "INPUT") {
          filter.search = event.target.value
        }
        eText.value = props.emptyText.replace('{search}', filter.search);
        // if (event.page) {
        //   pagination.value.page = event.page;
        //   loadData()
        // } else {
        //   loadData()
        // }
        setCurrent(1);
      },
      // 导出Excel
      toExcel (filterKeys, filename, field_names){
        Swal.fire({
          text: '导出数据可能会比较耗时，确定是否导出Excel？',
          icon: "warning",
          confirmButtonText: '确定',
          cancelButtonText: "取消",
          showCancelButton: true,
          closeOnConfirm: false,
        }).then ( ({isConfirmed}) => {
          if (isConfirmed) {
            let p = Object.assign(filter, {
              ordering: currentSort.value ? currentSort.value : sortingField.value,
              offset: 0,
              limit: 100000,
            })
            ApiService.get(props.apiUrl, p).then(({data})=>{
              if(data.count > 0){
                // let keys = Object.keys(data.results[0]);
                let keys = field_names ? field_names : filterKeys;
                exportJson2Excel(keys, formatJson(filterKeys, data.results), filename)
                Swal.close()
              }
            })
          }
        })
      },
      // 添加、修改按钮
      openDialog (item) {
        if ((formRef as any).value) {
          (formRef as any).value.clearValidate();
          (formRef as any).value.resetFields();
        }
        let init = JSON.parse(JSON.stringify(initData));
        if (item == undefined) {
          for (let k in init) {
            formData[k] = init[k]
          }
          if (props.addAction != undefined) props.addAction();
        } else {
          for (let j in init) {
            if (item[j] != undefined) formData[j] = item[j]
          }
          if (props.editAction != undefined) props.editAction(item);
        }
        dialogAddEdit.value = true
      },
      // 提交按钮
      submitDialog () {
        btnLoading.value = true;
        (formRef as any).value.validate((valid) => {
          if (valid) {
            let flag = true;  // 是否进行提交，即下面是提前阻止提交的判断
            if (props.submitAction != undefined) {
              flag = props.submitAction(function() {
                loadData()
                btnLoading.value = false
                dialogAddEdit.value = false
              });
            }
            if (flag) {
              if (formData.id > 0) {
                ApiService.update(props.apiUrl, formData.id, formData)
                .then((res) => {
                  ElMessage.success("更新成功！")
                  loadData()
                  btnLoading.value = false
                  dialogAddEdit.value = false
                  if (props.submitAfterAction != undefined) props.submitAfterAction(res.data);
                }).catch(()=>{
                  btnLoading.value = false
                });
              } else {
                ApiService.post(props.apiUrl, formData)
                .then((res) => {
                  ElMessage.success("新增成功！")
                  loadData()
                  btnLoading.value = false
                  dialogAddEdit.value = false
                  if (props.submitAfterAction != undefined) props.submitAfterAction(res.data);
                }).catch(()=>{
                  btnLoading.value = false
                });
              }
            } else {
              btnLoading.value = false
              return false
            }
          } else {
            btnLoading.value = false
            return false
          }
        })
      },
      // 删除按钮
      deleteAction (item) {
        if (props.delete == false && item.is_active != undefined && item.is_active == false){
          ElMessage.success("已禁用！")
        } else {
          Swal.fire({
            text: '确定是否删除?',
            icon: "warning",
            confirmButtonText: '确定',
            cancelButtonText: "取消",
            showCancelButton: true,
          }).then ( ({isConfirmed}) => {
            if (isConfirmed) {
              if (props.delete) {
                ApiService.delete(props.apiUrl + '/' + item.id)
                .then((res) => {
                  ElMessage.success("删除成功！")
                  loadData()
                })
              } else {
                ApiService.patch(props.apiUrl + '/' + item.id, {is_active: false})
                .then((res) => {
                  ElMessage.success("禁用成功！")
                  loadData()
                })
              }
            }
          });
        }
      },
    };
  },
});
